<template>
  <div class="warehouse-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>仓库管理</span>
          <el-button type="primary" @click="handleCreate">新增仓库</el-button>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="关键词">
          <el-input
            v-model="queryParams.keyword"
            placeholder="仓库名称/编码/地址"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 仓库列表 -->
      <el-table
        v-loading="loading"
        :data="warehouseList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="warehouseCode" label="仓库编码" width="120" />
        <el-table-column prop="warehouseName" label="仓库名称" width="150" />
        <el-table-column prop="address" label="地址" width="200" />
        <el-table-column prop="managerName" label="负责人" width="120" />
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" />
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑仓库对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form
        ref="warehouseFormRef"
        :model="warehouseForm"
        :rules="warehouseFormRules"
        label-width="100px"
      >
        <el-form-item label="仓库编码" prop="warehouseCode">
          <el-input v-model="warehouseForm.warehouseCode" />
        </el-form-item>
        <el-form-item label="仓库名称" prop="warehouseName">
          <el-input v-model="warehouseForm.warehouseName" />
        </el-form-item>
        <el-form-item label="地址" prop="address">
          <el-input v-model="warehouseForm.address" />
        </el-form-item>
        <el-form-item label="负责人" prop="managerName">
          <el-input v-model="warehouseForm.managerName" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="warehouseForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="warehouseForm.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getWarehouseList,
  getWarehouseDetail,
  createWarehouse,
  updateWarehouse,
  deleteWarehouse
} from '../api/internalWarehouse'

// 获取当前登录用户所属企业ID（与其他页面保持一致的实现方式）
const getCompanyId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr)
      return userInfo.companyId || null
    }
  } catch (e) {
    console.warn('解析用户信息失败', e)
  }
  return null
}

// 数据
const loading = ref(false)
const warehouseList = ref([])
const total = ref(0)
const queryParams = reactive({
  keyword: '',
  page: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const dialogTitle = ref('新增仓库')
const isEdit = ref(false)
const warehouseFormRef = ref(null)
const warehouseForm = reactive({
  id: null,
  warehouseCode: '',
  warehouseName: '',
  address: '',
  managerName: '',
  status: 1,
  remark: ''
})

const warehouseFormRules = {
  warehouseCode: [{ required: true, message: '请输入仓库编码', trigger: 'blur' }],
  warehouseName: [{ required: true, message: '请输入仓库名称', trigger: 'blur' }]
}

// 方法
const loadWarehouseList = async () => {
  loading.value = true
  try {
    const params = { ...queryParams }
    // 按照接口约定，显式传递当前登录者所属企业ID
    const companyId = getCompanyId()
    if (companyId) {
      params.companyId = companyId
    }
    const res = await getWarehouseList(params)
    if (res.code === '000000') {
      warehouseList.value = res.data.list || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取仓库列表失败:', error)
    ElMessage.error('获取仓库列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  queryParams.page = 1
  loadWarehouseList()
}

const handleReset = () => {
  queryParams.keyword = ''
  queryParams.page = 1
  loadWarehouseList()
}

// 生成仓库编码
const generateWarehouseCode = () => {
  const now = new Date()
  const year = now.getFullYear().toString().slice(-2)
  const month = (now.getMonth() + 1).toString().padStart(2, '0')
  const day = now.getDate().toString().padStart(2, '0')
  const timestamp = now.getTime().toString().slice(-6)
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
  return `WH${year}${month}${day}${timestamp}${random}`
}

const handleCreate = () => {
  dialogTitle.value = '新增仓库'
  isEdit.value = false
  Object.assign(warehouseForm, {
    id: null,
    warehouseCode: generateWarehouseCode(),
    warehouseName: '',
    address: '',
    managerName: '',
    status: 1,
    remark: ''
  })
  dialogVisible.value = true
}

const handleEdit = async (row) => {
  dialogTitle.value = '编辑仓库'
  isEdit.value = true
  try {
    const res = await getWarehouseDetail(row.id)
    if (res.code === '000000') {
      Object.assign(warehouseForm, res.data)
      dialogVisible.value = true
    }
  } catch (error) {
    console.error('获取仓库详情失败:', error)
    ElMessage.error('获取仓库详情失败')
  }
}

const handleSubmit = async () => {
  if (!warehouseFormRef.value) return
  await warehouseFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const res = isEdit.value 
          ? await updateWarehouse(warehouseForm) 
          : await createWarehouse(warehouseForm)
        if (res.code === '000000') {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          loadWarehouseList()
        }
      } catch (error) {
        console.error('保存仓库失败:', error)
        ElMessage.error(error.response?.data?.message || '保存仓库失败')
      }
    }
  })
}

const handleDialogClose = () => {
  warehouseFormRef.value?.resetFields()
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除该仓库吗？', '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    const res = await deleteWarehouse(row.id)
    if (res.code === '000000') {
      ElMessage.success('删除成功')
      loadWarehouseList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除仓库失败:', error)
      ElMessage.error(error.response?.data?.message || '删除仓库失败')
    }
  }
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadWarehouseList()
}

const handlePageChange = (page) => {
  queryParams.page = page
  loadWarehouseList()
}

// 生命周期
onMounted(() => {
  loadWarehouseList()
})
</script>

<style scoped>
.warehouse-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>

